import { ISavV2 } from '@/models/savV2';
import { AlertCircle, Calendar, MapPin, Gauge, FileText } from 'lucide-react';
import { formatIncidentAddress } from '@/utils/savV2Helpers';
import { formatDate, formatDateTime } from '@/utils/dateUtils';
import { getVehiclePurchaseDate } from '@/utils/contractHelpers';
import { formatSymptom, formatMileage, googleMapsUrl } from '@/utils/formatters';
import { SavLocationMap } from './SavLocationMap';

interface DeclarationStepProps {
  sav: ISavV2;
  completedAt?: string | null;
}

export function DeclarationStep({ sav, completedAt }: DeclarationStepProps) {
  const incidentAddress = formatIncidentAddress(sav);
  const hasLocation = !!(sav.incidentLatitude && sav.incidentLongitude);
  const company = sav.contract?.company;

  return (
    <div className="tw-bg-white tw-rounded-xl tw-border tw-border-gray-200 tw-shadow-sm tw-overflow-hidden">
      <h3 className="tw-flex tw-items-center tw-gap-2 tw-text-base tw-font-semibold tw-text-gray-900 tw-uppercase tw-px-4 tw-py-3 tw-border-b tw-border-gray-200 tw-m-0">
        <FileText className="tw-w-4 tw-h-4 tw-text-affinicar-primary" />
        Déclaration
        {completedAt && (
          <span className="tw-ml-auto tw-text-xs tw-font-normal tw-normal-case tw-text-gray-500">
            Terminée le {new Date(completedAt).toLocaleDateString('fr-FR')} à {new Date(completedAt).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}
          </span>
        )}
      </h3>
      <div className="tw-p-4 sm:tw-p-6 tw-space-y-4">
      <div>
        <div className="tw-space-y-3 tw-text-sm">
          <div className="tw-grid tw-grid-cols-1 sm:tw-grid-cols-2 tw-gap-3">
            <div className="tw-flex tw-items-start tw-gap-2">
              <Calendar className="tw-w-4 tw-h-4 tw-text-gray-400 tw-mt-0.5 tw-flex-shrink-0" />
              <div>
                <span className="tw-text-xs tw-text-gray-500 tw-uppercase">Date de déclaration</span>
                <p className="tw-text-sm tw-font-medium tw-text-gray-900 tw-mb-0">{sav.declarationDate ? formatDateTime(sav.declarationDate) : '—'}</p>
              </div>
            </div>

            <div className="tw-flex tw-items-start tw-gap-2">
              <Calendar className="tw-w-4 tw-h-4 tw-text-gray-400 tw-mt-0.5 tw-flex-shrink-0" />
              <div>
                <span className="tw-text-xs tw-text-gray-500 tw-uppercase">Date de la panne</span>
                <p className="tw-text-sm tw-font-medium tw-text-gray-900 tw-mb-0">{formatDate(sav.incidentDate)}</p>
              </div>
            </div>

            {getVehiclePurchaseDate(sav.contract) && (
              <div className="tw-flex tw-items-start tw-gap-2">
                <Calendar className="tw-w-4 tw-h-4 tw-text-gray-400 tw-mt-0.5 tw-flex-shrink-0" />
                <div>
                  <span className="tw-text-xs tw-text-gray-500 tw-uppercase">Date d'achat du véhicule</span>
                  <p className="tw-text-sm tw-font-medium tw-text-gray-900 tw-mb-0">{formatDate(getVehiclePurchaseDate(sav.contract))}</p>
                </div>
              </div>
            )}
          </div>

          <div className="tw-grid tw-grid-cols-1 sm:tw-grid-cols-2 tw-gap-3">
            {sav.mileage != null && (
              <div className="tw-flex tw-items-start tw-gap-2">
                <Gauge className="tw-w-4 tw-h-4 tw-text-gray-400 tw-mt-0.5 tw-flex-shrink-0" />
                <div>
                  <span className="tw-text-xs tw-text-gray-500 tw-uppercase">Kilométrage déclaré</span>
                  <p className="tw-text-sm tw-font-medium tw-text-gray-900 tw-mb-0">{formatMileage(sav.mileage)}</p>
                </div>
              </div>
            )}

            {sav.symptoms && sav.symptoms.length > 0 && (
              <div className="tw-flex tw-items-start tw-gap-2">
                <AlertCircle className="tw-w-4 tw-h-4 tw-text-gray-400 tw-mt-0.5 tw-flex-shrink-0" />
                <div>
                  <span className="tw-text-xs tw-text-gray-500 tw-uppercase tw-block tw-mb-1.5">Symptômes</span>
                  <div className="tw-flex tw-flex-wrap tw-gap-1.5">
                    {sav.symptoms.map((symptom, index) => (
                      <span key={index} className="tw-px-2.5 tw-py-1 tw-bg-orange-50 tw-text-orange-800 tw-border tw-border-orange-200 tw-rounded-full tw-text-xs tw-font-medium">
                        {formatSymptom(symptom)}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>

          {sav.description && (
            <div className="tw-flex tw-items-start tw-gap-2">
              <FileText className="tw-w-4 tw-h-4 tw-text-gray-400 tw-mt-0.5 tw-flex-shrink-0" />
              <div>
                <span className="tw-text-xs tw-text-gray-500 tw-uppercase tw-block tw-mb-1">Description</span>
                <p className="tw-text-sm tw-text-gray-700 tw-italic tw-mb-0 tw-bg-gray-50 tw-rounded-lg tw-p-3">"{sav.description}"</p>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Localisation */}
      {(hasLocation || incidentAddress) && (
        <div className="tw-flex tw-items-start tw-gap-2">
          <MapPin className="tw-w-4 tw-h-4 tw-text-gray-400 tw-mt-0.5 tw-flex-shrink-0" />
          <div className="tw-flex-1 tw-min-w-0">
            <span className="tw-text-xs tw-text-gray-500 tw-uppercase tw-block tw-mb-1.5">Localisation de l'incident</span>
            {incidentAddress && (
              <p className="tw-text-sm tw-text-gray-600 tw-mb-2">
                <a href={googleMapsUrl(incidentAddress)} target="_blank" rel="noopener noreferrer" className="tw-text-affinicar-primary hover:tw-underline tw-no-underline">
                  {incidentAddress}
                </a>
              </p>
            )}
            {hasLocation && sav.incidentLatitude && sav.incidentLongitude && (
              <SavLocationMap
                incidentLat={sav.incidentLatitude}
                incidentLng={sav.incidentLongitude}
                garantLat={company?.lat}
                garantLng={company?.lng}
                garantName={company?.name}
                height="200px"
              />
            )}
          </div>
        </div>
      )}
      </div>
    </div>
  );
}
